import { supabase } from '../lib/supabase';

export const unitService = {
    getAll: async ({ trainerOnly = false, currentOnly = trainerOnly } = {}) => {
        try {
            // 1. Fetch units (remove problematic join)
            const { data: scopedRows, error: unitsError } = await supabase.rpc(trainerOnly ? 'get_my_assigned_units' : 'get_scoped_units');

            if (unitsError) throw unitsError;
            let unitsData = (scopedRows || []).map(row => row.unit || row).sort((a,b) => String(a.name || '').localeCompare(String(b.name || '')));

            // 1.5 Fetch assignments separately to avoid "ambiguous relationship" error
            let assignmentsMap = {};
            try {
                // Fetch all active/relevant assignments
                let assignmentQuery = supabase
                    .from('unit_assignments')
                    .select('id, unit_id, status, trainer_id');
                if (trainerOnly) {
                    const { data: authData, error: authError } = await supabase.auth.getUser();
                    if (authError) throw authError;
                    assignmentQuery = assignmentQuery.eq('trainer_id', authData?.user?.id).ilike('status', 'active');
                }
                const { data: allAssignments, error: assignError } = await assignmentQuery;

                if (!assignError && allAssignments) {
                    allAssignments.forEach(a => {
                        if (!assignmentsMap[a.unit_id]) assignmentsMap[a.unit_id] = [];
                        assignmentsMap[a.unit_id].push(a);
                    });
                }
            } catch (err) {
                console.warn('Failed to fetch assignments:', err);
            }

            if (trainerOnly) {
                const assignedUnitIds = new Set(Object.entries(assignmentsMap)
                    .filter(([, rows]) => rows.some(row => String(row.status).toLowerCase() === 'active'))
                    .map(([unitId]) => String(unitId)));
                const loadedIds = new Set(unitsData.map(unit => String(unit.id)));
                // The current-only RPC deliberately omits historical assigned
                // units. Do not fetch those IDs back into the trainer view.
                const visibleAssignedUnitIds = currentOnly
                    ? new Set([...assignedUnitIds].filter(unitId => loadedIds.has(unitId)))
                    : assignedUnitIds;
                const missingIds = [...visibleAssignedUnitIds].filter(unitId => !loadedIds.has(unitId));
                if (missingIds.length && !currentOnly) {
                    const { data: missingUnits, error: missingError } = await supabase
                        .from('units')
                        .select('*')
                        .in('id', missingIds);
                    if (!missingError) unitsData = [...unitsData, ...(missingUnits || [])];
                }
                const currentUnitIds = new Set(unitsData.map(unit => String(unit.id)));
                const assignedAnchors = unitsData.filter(unit => visibleAssignedUnitIds.has(String(unit.id)));
                const groupCodes = [...new Set(assignedAnchors.map(unit => unit.teaching_group_code).filter(Boolean))];
                let groupMembers = [];
                if (groupCodes.length) {
                    const { data: memberRows, error: memberError } = await supabase
                        .from('units')
                        .select('*')
                        .in('teaching_group_code', groupCodes);
                    if (!memberError) {
                        groupMembers = (memberRows || []).filter(member => !currentOnly || currentUnitIds.has(String(member.id)));

                        // Paired units are loaded directly from `units`, so they
                        // do not carry the nested program -> department object
                        // returned by get_my_assigned_units(). Hydrate that
                        // relationship before merging them into the trainer list.
                        const programIds = [...new Set(groupMembers.map(member => member.program_id).filter(Boolean))];
                        if (programIds.length) {
                            const { data: programRows, error: programError } = await supabase
                                .from('programs')
                                .select('id, name, code, department_id')
                                .in('id', programIds);
                            if (!programError && programRows?.length) {
                                const departmentIds = [...new Set(programRows.map(program => program.department_id).filter(Boolean))];
                                const { data: departmentRows, error: departmentError } = departmentIds.length
                                    ? await supabase.from('departments').select('id, name, code').in('id', departmentIds)
                                    : { data: [], error: null };
                                const departmentsById = new Map((departmentRows || []).map(department => [String(department.id), department]));
                                const programsById = new Map(programRows.map(program => [String(program.id), {
                                    ...program,
                                    department: departmentsById.get(String(program.department_id)) || null,
                                }]));
                                if (!departmentError) {
                                    groupMembers = groupMembers.map(member => {
                                        const program = programsById.get(String(member.program_id));
                                        return program ? { ...member, program } : member;
                                    });
                                }
                            }
                        }
                    }
                }
                const visibleIds = new Set([...visibleAssignedUnitIds, ...groupMembers.map(unit => String(unit.id))]);
                const unitMap = new Map([...unitsData, ...groupMembers].map(unit => [String(unit.id), unit]));
                unitsData = [...unitMap.values()].filter(unit => visibleIds.has(String(unit.id)));
                groupMembers.forEach(member => {
                    if (assignmentsMap[member.id]?.length) return;
                    const anchor = assignedAnchors.find(unit => unit.teaching_group_code && unit.teaching_group_code === member.teaching_group_code);
                    if (anchor && assignmentsMap[anchor.id]) assignmentsMap[member.id] = assignmentsMap[anchor.id];
                });
            }

            // Use the canonical delivery function. It counts active/admitted/enrolled
            // students for the unit's programme and level, including combined groups.
            const enrollmentEntries = await Promise.all((unitsData || []).map(async unit => {
                const { data, error } = await supabase.rpc('get_unit_delivery_requirements', { target_unit: String(unit.id) });
                if (error) {
                    console.warn(`Enrollment count unavailable for ${unit.code}:`, error.message);
                    return [unit.id, 0];
                }
                return [unit.id, Number(data?.[0]?.enrolled_students || 0)];
            }));
            const enrollmentCounts = Object.fromEntries(enrollmentEntries);

            // 3. Extract trainer IDs from both active and pending assignments so
            // leadership can see who is awaiting mother-HOD approval.
            const trainerIds = new Set();
            Object.values(assignmentsMap).forEach(list => {
                list.forEach(a => {
                    if (['active', 'pending'].includes(String(a.status).toLowerCase()) && a.trainer_id) trainerIds.add(a.trainer_id);
                });
            });

            // 4. Fetch trainer profiles if needed
            let trainerMap = {};
            if (trainerIds.size > 0) {
                let trainers = [];
                let trainersError = null;
                if (!trainerOnly) {
                    const result = await supabase.rpc('get_assignable_trainers');
                    trainersError = result.error;
                    trainers = (result.data || []).map(row => row.trainer || row)
                        .filter(row => trainerIds.has(row.id));
                }
                if (trainersError || trainers.length === 0) {
                    const direct = await supabase
                        .from('user_profiles')
                        .select('*')
                        .in('id', Array.from(trainerIds));
                    trainersError = direct.error;
                    trainers = direct.data || [];
                }

                if (!trainersError && trainers) {
                    trainers.forEach(t => {
                        trainerMap[t.id] = t;
                    });
                }
            }

            // 5. Resolve the actual members behind each internal teaching-group key.
            const teachingGroups = (unitsData || []).reduce((groups, item) => {
                if (item.allow_combined_delivery && item.teaching_group_code) {
                    if (!groups[item.teaching_group_code]) groups[item.teaching_group_code] = [];
                    groups[item.teaching_group_code].push({ id: item.id, code: item.code, name: item.name });
                }
                return groups;
            }, {});
            // Backward compatibility: older rows stored the anchor unit code itself
            // as the group key without updating that anchor row.
            Object.keys(teachingGroups).forEach(groupCode => {
                const anchorCode = String(groupCode).replace(/^GROUP-/i, '');
                const anchor = unitsData.find(item => String(item.code).toUpperCase() === anchorCode.toUpperCase());
                if (anchor && !teachingGroups[groupCode].some(member => member.id === anchor.id)) {
                    teachingGroups[groupCode].unshift({ id: anchor.id, code: anchor.code, name: anchor.name });
                }
            });

            // 6. Merge data
            return unitsData.map(unit => {
                const assignments = assignmentsMap[unit.id] || [];
                const activeAssignment = assignments.find(a => String(a.status).toLowerCase() === 'active');
                const pendingAssignment = assignments.find(a => String(a.status).toLowerCase() === 'pending');
                const effectiveAssignment = activeAssignment || pendingAssignment;

                let trainerDetails = null;
                if (effectiveAssignment?.trainer_id && trainerMap[effectiveAssignment.trainer_id]) {
                    const t = trainerMap[effectiveAssignment.trainer_id];
                    trainerDetails = {
                        id: t.id,
                        fullName: t.full_name,
                        email: t.email,
                        department: t.department // if available on profile
                    };
                }

                return {
                    ...unit,
                    enrolled: enrollmentCounts[unit.id] || 0,
                    programId: unit.program_id,
                    unitType: unit.type || unit.unit_type || 'Core',
                    creditHours: unit.credit_hours || unit.credits,
                    hoursPerWeek: unit.hours_per_week,
                    examTheoryHours: unit.exam_theory_hours,
                    examPracticalHours: unit.exam_practical_hours,
                    sessionLengthMinutes: unit.session_length_minutes,
                    isActive: unit.is_active !== false,
                    programLevel: unit.program_level,
                    module: unit.module_in_level || unit.module || 1,
                    roomType: unit.required_room_type || unit.room_type || 'Standard Classroom',
                    teachingGroupCode: unit.teaching_group_code || '',
                    teachingGroupMembers: unit.teaching_group_code ? (teachingGroups[unit.teaching_group_code] || []) : [],
                    allowCombinedDelivery: Boolean(unit.allow_combined_delivery),
                    trainerAssignment: effectiveAssignment ? {
                        id: effectiveAssignment.id,
                        status: effectiveAssignment.status,
                        trainer: trainerDetails
                    } : { trainer: null }
                };
            });

        } catch (error) {
            console.error('Error fetching units:', error);
            return [];
        }
    },

    getById: async (id) => {
        try {
            // 1. Fetch unit basic info
            const { data: scopedRows, error } = await supabase.rpc('get_scoped_units');
            if (error) throw error;
            const data = (scopedRows || []).map(row => row.unit || row).find(row => String(row.id) === String(id));
            if (!data) throw new Error('Unit is outside your organisational scope.');

            // 1.5 Fetch assignments separately
            let assignments = [];
            try {
                const { data: assignData } = await supabase
                    .from('unit_assignments')
                    .select('id, unit_id, status, trainer_id')
                    .eq('unit_id', id);
                if (assignData) assignments = assignData;
            } catch (err) { }

            const activeAssignment = assignments.find(a => String(a.status).toLowerCase() === 'active');
            const pendingAssignment = assignments.find(a => String(a.status).toLowerCase() === 'pending');
            const effectiveAssignment = activeAssignment || pendingAssignment;

            let trainerDetails = null;
            if (activeAssignment && activeAssignment.trainer_id) {
                // Fetch trainer details
                const { data: trainer } = await supabase
                    .from('user_profiles')
                    .select('*')
                    .eq('id', activeAssignment.trainer_id)
                    .single();

                if (trainer) {
                    trainerDetails = {
                        id: trainer.id,
                        fullName: trainer.full_name,
                        email: trainer.email,
                        department: trainer.department // Assuming this field exists or is attached
                    };
                }
            }

            const { data: deliveryData, error: deliveryError } = await supabase.rpc('get_unit_delivery_requirements', { target_unit: String(id) });
            if (deliveryError) console.warn('Unable to load unit enrollment count:', deliveryError.message);

            return {
                ...data,
                enrolled: Number(deliveryData?.[0]?.enrolled_students || 0),
                programId: data.program_id,
                unitType: data.unit_type || 'Core',
                creditHours: data.credit_hours,
                hoursPerWeek: data.hours_per_week,
                examTheoryHours: data.exam_theory_hours,
                examPracticalHours: data.exam_practical_hours,
                sessionLengthMinutes: data.session_length_minutes,
                isActive: data.is_active !== false,
                trainerAssignment: effectiveAssignment ? {
                    id: effectiveAssignment.id,
                    status: effectiveAssignment.status,
                    trainer: trainerDetails
                } : { trainer: null }
            };
        } catch (error) {
            console.error('Error fetching unit:', error);
            return null;
        }
    },

    create: async (unitData) => {
        // Map camelCase to snake_case
        const dbData = {
            code: unitData.code,
            name: unitData.name,
            program_id: unitData.programId,
            credit_hours: unitData.creditHours,
            tuition_fee: unitData.tuitionFee,
            exam_fee: unitData.examFee,
            unit_type: String(unitData.unitType || 'Core'),
            classification: String(unitData.unitType || 'Core').toLowerCase(),
            program_level: Number(unitData.programLevel),
            module: Number(unitData.module),
            module_in_level: Number(unitData.module),
            required_room_type: unitData.roomType,
            room_type: unitData.roomType,
            teaching_group_code: unitData.teachingGroupCode || null,
            allow_combined_delivery: Boolean(unitData.allowCombinedDelivery),
            session_length_minutes: Number(unitData.sessionLengthMinutes || 90),
            hours_per_week: Number(unitData.hoursPerWeek || 4),
            is_active: unitData.isActive !== false,
            exam_theory_hours: unitData.examTheoryHours !== undefined ? Number(unitData.examTheoryHours) : 0,
            exam_practical_hours: unitData.examPracticalHours !== undefined ? Number(unitData.examPracticalHours) : 0,
            // Add other fields mapping as necessary
            // Assuming common schema
        };

        const { data, error } = await supabase
            .from('units')
            .insert([dbData])
            .select()
            .single();

        if (error) throw error;
        return { success: true, unit: data };
    },

    update: async (id, unitData) => {
        const dbData = {
            code: unitData.code,
            name: unitData.name,
            program_id: unitData.programId,
            credit_hours: unitData.creditHours,
            tuition_fee: unitData.tuitionFee,
            exam_fee: unitData.examFee,
            unit_type: unitData.unitType,
            classification: unitData.classification || (unitData.unitType ? String(unitData.unitType).toLowerCase() : undefined),
            program_level: unitData.programLevel,
            module: unitData.module,
            module_in_level: unitData.module,
            required_room_type: unitData.roomType,
            room_type: unitData.roomType,
            teaching_group_code: unitData.teachingGroupCode,
            allow_combined_delivery: unitData.allowCombinedDelivery,
            session_length_minutes: unitData.sessionLengthMinutes,
            hours_per_week: unitData.hoursPerWeek,
            is_active: unitData.isActive !== false,
            exam_theory_hours: unitData.examTheoryHours !== undefined ? Number(unitData.examTheoryHours) : undefined,
            exam_practical_hours: unitData.examPracticalHours !== undefined ? Number(unitData.examPracticalHours) : undefined,
        };

        const { data, error } = await supabase.rpc('update_scoped_academic_unit', {
            target_unit: id,
            payload: dbData,
        });

        if (error) throw error;
        if (!data) throw new Error('The unit was not updated. Check your permissions and retry.');
        return { success: true, unit: data };
    },

    setUnitPairing: async (id, groupCode, enabled = true) => {
        if (!id || (enabled && !groupCode)) throw new Error('Pairing unit and group are required');
        const { data, error } = await supabase.rpc('update_scoped_academic_unit', {
            target_unit: id,
            payload: {
                allow_combined_delivery: Boolean(enabled),
                teaching_group_code: enabled ? groupCode : null,
            },
        });
        if (error) throw error;
        return data;
    },

    synchronizeTeachingGroup: async (groupCode, anchorUnitId) => {
        if (!groupCode || !anchorUnitId) throw new Error('A teaching group and anchor unit are required');
        const { data, error } = await supabase.rpc('synchronize_combined_delivery_group', {
            target_group: groupCode,
            anchor_unit: anchorUnitId,
        });
        if (error) throw error;
        return data;
    },

    delete: async (id) => {
        const { error } = await supabase
            .from('units')
            .delete()
            .eq('id', id);

        if (error) throw error;
        return { success: true };
    },

    updateTrainerWorkload: async (trainerId, { maxUnits, maxHours }) => {
        try {
            const { data, error } = await supabase.rpc('set_trainer_workload', {
                target_trainer: trainerId,
                max_units: maxUnits !== undefined ? (maxUnits || null) : null,
                max_hours: maxHours !== undefined ? (maxHours || null) : null
            });

            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error updating trainer workload:', error);
            throw error;
        }
    },

    getAssignableTrainers: async () => {
        const { data, error } = await supabase.rpc('get_assignable_trainers');
        if (error) throw error;
        return (data || []).map(row => row.trainer || row);
    },

    assignTrainer: async (unitId, trainerId) => {
        try {
            const { data, error } = await supabase.rpc('assign_unit_trainer', {
                target_unit: unitId,
                target_trainer: trainerId
            });

            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error assigning trainer:', error);
            throw error;
        }
    },

    unassignTrainer: async (unitId) => {
        try {
            const { error } = await supabase.rpc('unassign_unit_trainer', {
                target_unit: unitId
            });

            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('Error unassigning trainer:', error);
            throw error;
        }
    },

    updateTrainerAssignment: async (assignmentId, assignmentData) => {
        const trainerId = typeof assignmentData === 'string'
            ? assignmentData
            : assignmentData?.trainerId;
        const unitId = assignmentData?.unitId;
        if (!assignmentId || !trainerId || !unitId) {
            throw new Error('Unit, assignment and trainer are required');
        }

        const { data, error } = await supabase.rpc('assign_unit_trainer', {
            target_unit: unitId,
            target_trainer: trainerId
        });

        if (error) throw error;
        return { success: true, data };
    },

    getPendingDepartmentApprovals: async () => {
        const { data, error } = await supabase.rpc('get_unit_assignment_approvals');
        if (error) throw error;
        return (data || []).map(row => row.approval || row);
    },

    decideTrainerAssignment: async (requestId, approve, reason = null) => {
        const { data, error } = await supabase.rpc('decide_unit_assignment', {
            request_id: requestId,
            approve: Boolean(approve),
            reason
        });
        if (error) throw error;
        return data;
    },

    createBulk: async (unitsData) => {
        // unitsData should be an array of unit objects
        // We assume they are already formatted for DB (snake_case) or we map them here.
        // Let's assume the caller maps them or we map simple fields.

        const dbData = unitsData.map(u => ({
            code: u.code,
            name: u.name,
            program_id: u.programId,
            credit_hours: u.creditHours,
            unit_type: u.unitType || 'Core',
            classification: String(u.unitType || 'Core').toLowerCase(),
            program_level: Number(u.programLevel),
            session_length_minutes: u.sessionLengthMinutes || 90,
            hours_per_week: u.hoursPerWeek || 4,
            room_type: u.roomType || 'Lecture',
            module: u.module || 1,
            module_in_level: u.module || 1,
            required_room_type: u.roomType || 'Standard Classroom',
            teaching_group_code: u.teachingGroupCode || null,
            allow_combined_delivery: Boolean(u.allowCombinedDelivery),
            is_active: u.isActive !== false,
            // default capacity if needed
        }));

        const { data, error } = await supabase
            .from('units')
            .insert(dbData)
            .select();

        if (error) throw error;
        return { success: true, count: data.length };
    }
};
